import * as React from 'react';
import { useEffect, useState } from 'react';

import { useApolloClient, useQuery } from '@apollo/client';
import FoldersQuery from '@graphql/queries/FoldersQuery';
import ProductByIdQuery from '@graphql/queries/ProductByIdQuery';
import ProductsQuery from '@graphql/queries/ProductsQuery';
import { ContentCopy, Image, Search as SearchIcon } from '@mui/icons-material';
import {
  CircularProgress,
  IconButton,
  Input,
  InputAdornment,
  LinearProgress,
  Paper,
  Tooltip,
} from '@mui/material';
import MaterialButton from 'components/MaterialButton';
import RDTFirstPage from 'components/ReactDataTable/RDTFirstPage';
import RDTLastPage from 'components/ReactDataTable/RDTLastPage';
import RTDNextPage from 'components/ReactDataTable/RDTNextPage';
import RDTPrevPage from 'components/ReactDataTable/RDTPrevPage';
import { handleAzureMigratedImages } from 'containers/Product/helper';
import formatDate from 'date-fns/format';
import parseISO from 'date-fns/parseISO';
import useWindowSize from 'hooks/useWindowSize';
import { get } from 'lodash';
import queryString from 'query-string';
import DataTable, {
  SortOrder,
  TableColumn,
  TableStyles,
} from 'react-data-table-component';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Link, useLocation, useHistory } from 'react-router-dom';
import selectProductFolderId from 'selectors/productFolderId';
import selectWorkspaceProducerId from 'selectors/workspaceProducerId';
import { useCustomerPreferences } from 'services/customerPreferences';

import styles from './ProductTable.module.css';
import ProductSearch from '../../components/ProductSearch';
import {
  PRODUCT,
  PRODUCT_CREATE,
  PRODUCT_CREATE_BULK,
} from '../../constants/routePaths';
import i18n from '../../i18n';
import { PageHeader } from '../../styles/themeComponents';
import { buildCloneData } from '../../utils/buildCloneData';
import formatPath from '../../utils/formatPath';
import ConditionViewerRoleContainer from '../ConditionViewerRole';

const DATE_FORMAT = 'yyyy-MM-dd';


const productTableStyles: TableStyles = {
  table: {
    style: {
      maxHeight: 'none',
    },
  },
  rows: {
    style: {
      padding: '0.75rem',
    },
  },
};

const getFormattedProductData = (productResults) => {
  return (
    productResults &&
    productResults.products &&
    productResults.products.edges.map((edge) => ({
      id: edge.product.id,
      image: (
        <div className={styles.imageContainer}>
          {edge.product.productImages.totalCount ? (
            <img
              src={handleAzureMigratedImages(edge.product.productImages.nodes[0].url)}
              alt={`${edge.product.id}_image`}
              className={styles.image}
            />
          ) : (
            <Image className={styles.image} />
          )}
        </div>
      ),
      name: (
        <Link
          key={edge.product.id}
          className={`restore-${edge.product.id}`}
          to={{
            pathname: formatPath(PRODUCT, { productId: edge.product.id }),
            state: edge.product.id,
          }}
        >
          {edge.product.name}
        </Link>
      ),
      brand: edge.product.brand,
      date: formatDate(parseISO(edge.product.createdAt), DATE_FORMAT),
      category:
        edge.product.productCategory && edge.product.productCategory.name,
      panel: edge.product.panelProducts.nodes
        .filter(
          (panelProduct) =>
            panelProduct?.panel?.visibility !== false,
        )
        .map(
          (panelProduct) =>
            panelProduct && panelProduct.panel && panelProduct.panel.pin,
        )
        .join(', '),
      reviews: edge.product.productReviews.totalCount,
      lastReview:
        get(edge.product, 'productReviews.nodes.length') > 0
          ? formatDate(
            parseISO(edge.product.productReviews.nodes[0].createdAt),
            DATE_FORMAT,
          )
          : '',
    }))
  );
};

interface TableRow {
  id: number;
  image: string;
  name: string;
  brand: string;
  date: string;
  category: string;
  panel: string;
  reviews: string;
  lastReview: string;
  actions: React.ReactNode;
}

const ProductTable: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const history = useHistory();
  const client = useApolloClient();

  const [showSearch, setShowSearch] = useState<boolean>(false);
  const [searchString, setSearchString] = useState<string>('');
  const [cloningProductId, setCloningProductId] = useState<number | null>(null);

  const customerPreferences = useCustomerPreferences();

  const queryParams = queryString.parse(location.search);
  const initialPage = queryParams.page ? Number(queryParams.page) : 1;
  const initialSizePerPage = queryParams.size ? Number(queryParams.size) : 20;

  const [page, setPage] = useState<number>(initialPage);
  const [sizePerPage, setSizePerPage] = useState<number>(initialSizePerPage);
  const [orderBy, setOrderBy] = useState({
    order: 'desc',
    column: 'lastActive',
    key: 'ID_DESC',
  });
  const [formattedData, setFormattedData] = useState<any[]>([]);
  const [totalRows, setTotalRows] = useState<number>(0);

  const updateUrl = (newPage: number, newSize: number) => {
    const newQueryParams = queryString.stringify({
      page: newPage,
      size: newSize,
    });

    history.push({ search: `?${newQueryParams}` });
  };

  useEffect(() => {
    // Sync URL when page or sizePerPage changes
    const params = queryString.stringify({ page, size: sizePerPage });
    history.push({ search: params });
  }, [page, sizePerPage, history]);

  useEffect(() => {
    const queryParams = queryString.parse(location.search);
    const newPage = parseInt(queryParams.page as string, 10) || 1;
    const newSize = parseInt(queryParams.size as string, 10) || 20;

    if (newPage !== page || newSize !== sizePerPage) {
      setPage(newPage);
      setSizePerPage(newSize);
    }
  }, [location.search]);


  const {
    widthBelow600,
    widthBelow700,
    widthBelow800,
    widthBelow900,
    widthBelow1000,
    widthBelow1100,
    widthBelow1400,
  } = useWindowSize();

  const producerId = useSelector((state) => selectWorkspaceProducerId(state));
  const productFolderId = useSelector((state) => selectProductFolderId(state));

  const {
    loading: productsLoading,
    error: productsError,
    data: productResults,
    fetchMore,
  } = useQuery(ProductsQuery, {
    variables: {
      offset: (page - 1) * sizePerPage,
      first: sizePerPage,
      condition: {
        producerId: producerId,
        folderId: productFolderId !== 0 ? productFolderId : undefined,
        visibility: true,
      },
      orderBy: orderBy.key,
    },
    notifyOnNetworkStatusChange: true,
  });

  const {
    loading: foldersLoading,
    error: foldersError,
    data: folderResults,
  } = useQuery(FoldersQuery, {
    variables: {
      condition: {
        producerId: producerId,
      },
    },
  });

  useEffect(() => {
    if (productResults) {
      setTotalRows(productResults.products.totalCount);
      setFormattedData(getFormattedProductData(productResults));
    }
  }, [productResults, orderBy]);

  const hideSearch = () => {
    setSearchString('');
    setShowSearch(false);
  };

  const handleCloneProduct = async (productId: number) => {
    setCloningProductId(productId);
    try {
      const { data } = await client.query({
        query: ProductByIdQuery,
        variables: { id: productId },
      });

      if (data?.product) {
        const cloneData = buildCloneData(data.product, t);
        history.push({
          pathname: PRODUCT_CREATE,
          state: { cloneData },
        });
      }
    } catch (error) {
      console.error('Failed to fetch product for cloning:', error);
    } finally {
      setCloningProductId(null);
    }
  };

  const columns: TableColumn<TableRow>[] = [
    {
      id: 'id',
      selector: (row: TableRow) => row.id,
      name: 'Product ID',
      wrap: true,
      omit: true,
    },
    {
      id: 'image',
      selector: (row: TableRow) => row.image,
      name: <div className={styles.tableHeader}>{i18n.t('general.image')}</div>,
      wrap: true,
      omit: widthBelow1400,
    },
    {
      id: 'name',
      selector: (row: TableRow) => row.name,
      name: (
        <div className={styles.tableHeader}>
          {i18n.t('product.productName')}
        </div>
      ),
      sortable: true,
      sortField: 'NAME',
      wrap: true,
    },
    {
      id: 'brand',
      selector: (row: TableRow) => row.brand,
      name: (
        <div className={styles.tableHeader}>
          {i18n.t('product.productBrand')}
        </div>
      ),
      sortable: true,
      sortField: 'BRAND',
      wrap: true,
      omit: widthBelow600,
    },
    {
      id: 'date',
      selector: (row: TableRow) => row.date,
      name: (
        <div className={styles.tableHeader}>
          {i18n.t('general.dateCreated')}
        </div>
      ),
      sortable: true,
      sortField: 'CREATED_AT',
      wrap: true,
      omit: widthBelow1000,
    },
    {
      id: 'category',
      selector: (row: TableRow) => row.category,
      name: (
        <div className={styles.tableHeader}>
          {i18n.t('product.productCategory')}
        </div>
      ),
      wrap: true,
      omit: widthBelow700,
    },
    {
      id: 'panel',
      selector: (row: TableRow) => row.panel,
      name: (
        <div className={styles.tableHeader}>{i18n.t('product.panels')}</div>
      ),
      wrap: true,
      omit: widthBelow1100,
      style: styles.panelColumn,
    },
    {
      id: 'reviews',
      selector: (row: TableRow) => row.reviews,
      name: (
        <div className={styles.tableHeader}>{i18n.t('reviews.reviews')}</div>
      ),
      wrap: true,
      omit: widthBelow800,
    },
    {
      id: 'lastReview',
      selector: (row: TableRow) => row.lastReview,
      name: (
        <div className={styles.tableHeader}>{i18n.t('reviews.lastReview')}</div>
      ),
      wrap: true,
      omit: widthBelow900,
    },
    {
      id: 'actions',
      cell: (row: TableRow) => (
        <Tooltip title={t('product.cloneProduct')}>
          <span>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleCloneProduct(row.id);
              }}
              disabled={cloningProductId === row.id}
            >
              {cloningProductId === row.id ? (
                <CircularProgress size={18} />
              ) : (
                <ContentCopy fontSize="small" />
              )}
            </IconButton>
          </span>
        </Tooltip>
      ),
      name: '',
      width: '56px',
      right: true,
      omit: !customerPreferences.features.cloneProduct,
    },
  ];

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    updateUrl(newPage, sizePerPage);

    fetchMore({
      variables: {
        offset: (newPage - 1) * sizePerPage,
        first: sizePerPage,
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) return prev;
        return fetchMoreResult;
      },
    });
  };

  const handlePerRowsChange = async (newPerPage: number, page: number) => {
    setSizePerPage(newPerPage);
    setPage(1);
    updateUrl(1, newPerPage);
  };

  const handleSort = async (
    column: TableColumn<TableRow>,
    sortDirection: SortOrder,
  ) => {
    setOrderBy({
      order: sortDirection,
      column: column.id as string,
      key: `${column.sortField}_${sortDirection.toUpperCase()}`,
    });
  };

  if (!producerId) {
    return <div />;
  }

  if (productsLoading || foldersLoading) {
    return <LinearProgress />;
  }

  if (productsError || foldersError) {
    return <div>ERROR: Unable to fetch data!</div>;
  }

  return (
    <Paper className={styles.container}>
      <div className={styles.headerContainer}>
        <div className={styles.headerTextContainer}>
          <PageHeader>{t('navigation.products')}</PageHeader>
          <h3 className={styles.productTitle}>{t('product.gallery')}</h3>
        </div>
        <Input
          endAdornment={
            <InputAdornment position="end">
              <SearchIcon />
            </InputAdornment>
          }
          onChange={(event) => {
            setSearchString(event.target.value);
            setShowSearch(true);
          }}
          placeholder={t('general.search')}
        />
        <div>
          <Link to={PRODUCT_CREATE}>
            <MaterialButton variant="outlined" soft teal>
              {t('product.createProduct')}
            </MaterialButton>
          </Link>
          <ConditionViewerRoleContainer
            render={(viewerRoles) =>
              (viewerRoles.viewerRoleIsSuperadmin ||
                viewerRoles.viewerRoleIsAdmin) && (
                <Link to={PRODUCT_CREATE_BULK}>
                  <MaterialButton variant="outlined" soft teal>
                    {t('product.createProductsBulk')}
                  </MaterialButton>
                </Link>
              )
            }
          />
        </div>
      </div>
      {searchString.length > 0 && showSearch && (
        <ProductSearch
          query={searchString}
          hideSearch={hideSearch}
          first={10}
        />
      )}
      {productResults.loading || folderResults.loading ? (
        <LinearProgress />
      ) : (
        <DataTable
          customStyles={productTableStyles}
          columns={columns}
          data={formattedData}
          progressPending={productsLoading || foldersLoading}
          progressComponent={<LinearProgress color="secondary" />}
          persistTableHead
          striped
          pagination
          paginationIconFirstPage={<RDTFirstPage />}
          paginationIconLastPage={<RDTLastPage />}
          paginationIconPrevious={<RDTPrevPage />}
          paginationIconNext={<RTDNextPage />}
          paginationServer
          paginationRowsPerPageOptions={[10, 20, 30, 40, 50]}
          paginationPerPage={sizePerPage}
          onChangeRowsPerPage={handlePerRowsChange}
          onChangePage={handlePageChange}
          paginationTotalRows={totalRows}
          paginationDefaultPage={page}
          sortServer
          onSort={handleSort}
        />
      )}
    </Paper>
  );
};

export default ProductTable;
